import React, { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import {
    Paper, TextField, Button, Checkbox, Chip, Tooltip, IconButton, InputAdornment,
    Accordion, AccordionSummary, AccordionDetails, Collapse, Switch, FormControlLabel,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@material-ui/core'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import SyncIcon from '@material-ui/icons/Sync'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import InfoIcon from '@material-ui/icons/Info'
import ClearIcon from '@material-ui/icons/Clear'
import { setLoading, setSnackInfo } from '../../redux/actions/appAction'
import HttpService from '../../services/httpService'
import { getRegisteredRoutePaths } from '../../utils/routeAccess'

const NAVY = '#1C375D'
const ORANGE = '#F47E46'

const useStyles = makeStyles(() => ({
    root: { paddingBottom: 80 },

    header: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
    title: { fontSize: 22, fontWeight: 600, color: NAVY, margin: 0 },
    subtitle: { fontSize: 13, color: '#6b7a90', margin: '4px 0 0 0' },
    headerRight: { marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },

    note: {
        background: '#f0f6ff', border: '1px solid #cfe0f7', borderRadius: 8,
        padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#31506f', lineHeight: 1.7
    },

    card: {
        borderRadius: 10, marginBottom: 16, overflow: 'hidden',
        border: '1px solid #e4e8ef', boxShadow: '0 1px 3px rgba(16,36,64,0.06)'
    },
    cardHead: {
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px', borderBottom: '1px solid #eef1f5', background: '#fafbfd'
    },
    stepNo: {
        width: 24, height: 24, borderRadius: '50%', color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 600, flexShrink: 0
    },
    stepTitle: { fontWeight: 600, fontSize: 14 },
    cardBody: { padding: 16 },
    pushRight: { marginLeft: 'auto' },

    typeBox: { width: 190 },
    targetBox: { minWidth: 300, flex: '1 1 320px' },
    routeBox: { minWidth: 300, flex: '1 1 340px' },
    search: { minWidth: 220, flex: '1 1 220px' },

    stale: {
        background: '#fff4f2', border: '1px solid #f3c9c2', borderRadius: 8,
        padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#9c3a2c',
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', lineHeight: 1.6
    },

    module: {
        border: '1px solid #e4e8ef', borderRadius: 8, marginBottom: 8, boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '0 0 8px 0' },
    },
    moduleSummary: {
        minHeight: 48,
        '&.Mui-expanded': { minHeight: 48 },
        '& .MuiAccordionSummary-content': { margin: '8px 0', alignItems: 'center' },
        '& .MuiAccordionSummary-content.Mui-expanded': { margin: '8px 0' },
    },
    moduleName: { fontWeight: 600, color: NAVY, fontSize: 14 },

    routeGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        width: '100%', gap: 2
    },
    routeItem: {
        display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px',
        borderRadius: 6, cursor: 'pointer', overflow: 'hidden',
        '&:hover': { background: '#f4f7fb' }
    },
    routeItemOn: { background: '#fff3ec' },
    routeLabel: { fontSize: 13, color: '#2c3e55', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    routePath: { fontSize: 11, color: '#93a1b5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

    empty: { padding: 40, textAlign: 'center', color: '#8d9bb0', fontSize: 14 },

    sticky: {
        position: 'sticky', bottom: 0, zIndex: 5,
        background: '#fff', border: '1px solid #e4e8ef', borderRadius: 10,
        boxShadow: '0 -2px 10px rgba(16,36,64,0.08)',
        padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap'
    },
    dot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 6 },
}))

const ACCESS_TYPES = [
    { key: 'role', label: 'Roles' },
    { key: 'user', label: 'User' },
]

const routeFilter = createFilterOptions({
    limit: 60,
    stringify: (option) => `${option.label || ''} ${option.path}`,
})

const RouteAccessManagement = (props) => {
    const classes = useStyles()

    const [routes, setRoutes] = useState([])
    const [roles, setRoles] = useState([])
    const [users, setUsers] = useState([])

    const [accessType, setAccessType] = useState(ACCESS_TYPES[0])
    const [targets, setTargets] = useState([])

    const [selectedPaths, setSelectedPaths] = useState([])
    const [savedPaths, setSavedPaths] = useState([])
    const [search, setSearch] = useState('')
    const [onlySelected, setOnlySelected] = useState(false)
    const [expanded, setExpanded] = useState([])

    const [staleRoutes, setStaleRoutes] = useState([])
    const [missingCount, setMissingCount] = useState(0)
    const [removeModal, setRemoveModal] = useState(false)
    const [showHelp, setShowHelp] = useState(false)

    useEffect(() => {
        loadMasters()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const compareWithApp = (dbRoutes) => {
        const appPaths = getRegisteredRoutePaths()
        if (!appPaths.length) return
        const dbPaths = (dbRoutes || []).map(route => route.path)
        setStaleRoutes(dbPaths.filter(p => !appPaths.includes(p)))
        setMissingCount(appPaths.filter(p => !dbPaths.includes(p)).length)
    }

    const loadMasters = async () => {
        props.setLoading(true)
        try {
            const [routeRes, roleRes, userRes] = await Promise.all([
                HttpService.getAllRouteAccess(),
                HttpService.getAllRoles(),
                HttpService.getAllUsersforAcess({ isActive: true })
            ])
            setRoutes(routeRes.data || [])
            setRoles(roleRes.data || [])
            setUsers(userRes.data || [])
            compareWithApp(routeRes.data)
        } catch (error) {
            props.setSnackInfo('Could not load routes / roles / users', 'error')
        }
        props.setLoading(false)
    }

    const syncRoutes = async () => {
        const paths = getRegisteredRoutePaths()
        if (!paths.length) {
            return props.setSnackInfo('Could not read routes from the app - reload the page and try again', 'warning')
        }
        props.setLoading(true)
        try {
            const res = await HttpService.syncRouteAccess(paths)
            const added = (res.data && res.data.added) || 0
            const stale = (res.data && res.data.staleInDb) || []

            const routeRes = await HttpService.getAllRouteAccess()
            setRoutes(routeRes.data || [])
            compareWithApp(routeRes.data)
            if (stale.length) setStaleRoutes(stale)

            props.setSnackInfo(
                added > 0 ? `${added} new route${added > 1 ? 's' : ''} added` : 'All routes are already in the database',
                added > 0 ? 'success' : 'info'
            )
        } catch (error) {
            props.setSnackInfo('Sync failed', 'error')
        }
        props.setLoading(false)
    }

    const removeStaleRoutes = async () => {
        setRemoveModal(false)
        props.setLoading(true)
        try {
            const res = await HttpService.removeRouteAccess(staleRoutes)
            const removed = (res.data && res.data.removed) || 0

            const routeRes = await HttpService.getAllRouteAccess()
            setRoutes(routeRes.data || [])
            compareWithApp(routeRes.data)

            setSelectedPaths(prev => prev.filter(p => !staleRoutes.includes(p)))
            setSavedPaths(prev => prev.filter(p => !staleRoutes.includes(p)))

            props.setSnackInfo(`${removed} route${removed > 1 ? 's' : ''} removed`, 'success')
        } catch (error) {
            props.setSnackInfo('Could not remove routes', 'error')
        }
        props.setLoading(false)
    }

    const isRoleMode = accessType && accessType.key === 'role'
    const targetOptions = isRoleMode ? roles : users

    const targetLabel = (option) => {
        if (!option) return ''
        return isRoleMode ? (option.name || '') : `${option.name || ''} (${option.email || ''})`
    }

    const onTypeChange = (_e, value) => {
        setAccessType(value || ACCESS_TYPES[0])
        setTargets([])
        setSelectedPaths([])
        setSavedPaths([])
        setExpanded([])
    }

    const fetchPaths = async (id) => {
        const res = isRoleMode
            ? await HttpService.getRouteAccessByRole(id)
            : await HttpService.getRouteAccessByUser(id)
        return res.data.paths || []
    }

    // With several targets selected, the checked pages start as the ones every
    // target already has in common.
    const onTargetChange = async (_e, values) => {
        const list = values || []
        setTargets(list)
        setExpanded([])
        if (!list.length) {
            setSelectedPaths([])
            setSavedPaths([])
            return
        }
        props.setLoading(true)
        try {
            const all = await Promise.all(list.map(t => fetchPaths(t._id)))
            const common = all.reduce((acc, cur) => acc.filter(p => cur.includes(p)))
            setSelectedPaths(common)
            setSavedPaths(common)
        } catch (error) {
            props.setSnackInfo('Could not load access', 'error')
        }
        props.setLoading(false)
    }

    const togglePath = (path) => {
        setSelectedPaths(prev => (
            prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
        ))
    }

    const toggleModule = (modulePaths, allTicked) => {
        setSelectedPaths(prev => (
            allTicked
                ? prev.filter(p => !modulePaths.includes(p))
                : [...new Set([...prev, ...modulePaths])]
        ))
    }

    const onRoutesPicked = (_e, values) => setSelectedPaths(values.map(route => route.path))

    const selectedRouteObjects = useMemo(() => (
        selectedPaths.map(path => routes.find(route => route.path === path)).filter(Boolean)
    ), [selectedPaths, routes])

    const hasTarget = targets.length > 0
    const isMulti = targets.length > 1

    const save = async () => {
        if (!hasTarget) {
            return props.setSnackInfo(isRoleMode ? 'Select a role first' : 'Select a user first', 'warning')
        }
        props.setLoading(true)
        try {
            await Promise.all(targets.map(async (t) => {
                // several targets: only add the ticked pages, keep what each already has
                const paths = isMulti
                    ? [...new Set([...(await fetchPaths(t._id)), ...selectedPaths])]
                    : selectedPaths
                return isRoleMode
                    ? HttpService.saveRouteAccessForRole(t._id, paths)
                    : HttpService.saveRouteAccessForUser(t._id, paths)
            }))
            setSavedPaths(selectedPaths)
            props.setSnackInfo('Access saved', 'success')
        } catch (error) {
            props.setSnackInfo('Could not save access', 'error')
        }
        props.setLoading(false)
    }

    const grouped = useMemo(() => {
        const term = search.trim().toLowerCase()
        let filtered = routes
        if (term) {
            filtered = filtered.filter(r => (
                r.path.toLowerCase().includes(term) || (r.label || '').toLowerCase().includes(term)
            ))
        }
        if (onlySelected) filtered = filtered.filter(r => selectedPaths.includes(r.path))

        const byModule = {}
        filtered.forEach(route => {
            const key = route.module || 'General'
            if (!byModule[key]) byModule[key] = []
            byModule[key].push(route)
        })
        return Object.keys(byModule).sort().map(name => ({ name, routes: byModule[name] }))
    }, [routes, search, onlySelected, selectedPaths])

    const visibleCount = grouped.reduce((n, g) => n + g.routes.length, 0)
    const isFiltering = search.trim().length > 0 || onlySelected
    const allExpanded = grouped.length > 0 && expanded.length === grouped.length

    const isOpen = (name) => isFiltering || expanded.includes(name)
    const toggleOpen = (name) => setExpanded(prev => (
        prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    ))

    const isDirty = useMemo(() => (
        selectedPaths.length !== savedPaths.length || selectedPaths.some(p => !savedPaths.includes(p))
    ), [selectedPaths, savedPaths])

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div>
                    <h2 className={classes.title}>Route Access</h2>
                    <p className={classes.subtitle}>
                        Give a role or a user access to pages that are not in their sidebar menu
                    </p>
                </div>
                <div className={classes.headerRight}>
                    <Chip size="small" variant="outlined" label={`${routes.length} routes`} />
                    {missingCount > 0 && (
                        <Tooltip title="These routes exist in the app but not in the database - click Sync">
                            <Chip size="small" color="secondary" label={`${missingCount} pending sync`} />
                        </Tooltip>
                    )}
                    <Tooltip title="Add any new pages from the app into the database">
                        <Button variant="outlined" size="small" startIcon={<SyncIcon />} onClick={syncRoutes}>
                            Sync routes
                        </Button>
                    </Tooltip>
                    <Tooltip title="How this screen works">
                        <IconButton size="small" onClick={() => setShowHelp(v => !v)}>
                            <InfoIcon fontSize="small" style={{ color: showHelp ? ORANGE : '#9aa7ba' }} />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            <Collapse in={showHelp}>
                <div className={classes.note}>
                    Pages that are already in a user's sidebar menu (Access Management) stay available
                    to them &mdash; you do not need to tick those here.
                    <br />
                    This screen grants <b>extra access on top of that</b>, mainly for pages that never
                    appear in the menu and are opened from a button, such as <code>/performance_target</code>.
                    <br />
                    Added a new page to the app? Click <b>Sync routes</b> and it will appear in the list below.
                </div>
            </Collapse>

            {staleRoutes.length > 0 && (
                <div className={classes.stale}>
                    <span>
                        <b>
                            {staleRoutes.length} route{staleRoutes.length > 1 ? 's' : ''} in the database
                            no longer exist{staleRoutes.length > 1 ? '' : 's'} in the app
                        </b>
                        &nbsp;&mdash; those pages were probably removed. They have no effect, but you can clean them up.
                    </span>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setRemoveModal(true)}
                        className={classes.pushRight}
                    >
                        Remove from database
                    </Button>
                </div>
            )}

            {/* ------------------------- step 1 ------------------------- */}
            <Paper className={classes.card} elevation={0}>
                <div className={classes.cardHead}>
                    <span className={classes.stepNo} style={{ background: NAVY }}>1</span>
                    <span className={classes.stepTitle} style={{ color: NAVY }}>Choose who gets access</span>
                    {hasTarget && (
                        <Chip
                            size="small"
                            className={classes.pushRight}
                            label={`${isRoleMode ? 'Roles' : 'Users'}: ${targets.length} selected`}
                            onDelete={() => onTargetChange(null, [])}
                            style={{ background: '#eaf1fb', color: NAVY, fontWeight: 500 }}
                        />
                    )}
                </div>
                <div className={classes.cardBody} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Autocomplete
                        className={classes.typeBox}
                        size="small"
                        disableClearable
                        options={ACCESS_TYPES}
                        value={accessType}
                        getOptionLabel={(option) => option.label}
                        getOptionSelected={(option, value) => option.key === value.key}
                        onChange={onTypeChange}
                        renderInput={(params) => (
                            <TextField {...params} label="Give access to" variant="outlined" />
                        )}
                    />
                    <Autocomplete
                        className={classes.targetBox}
                        size="small"
                        multiple
                        disableCloseOnSelect
                        limitTags={3}
                        options={targetOptions}
                        value={targets}
                        getOptionLabel={targetLabel}
                        getOptionSelected={(option, value) => option._id === value._id}
                        onChange={onTargetChange}
                        noOptionsText={isRoleMode ? 'No role found' : 'No user found'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={isRoleMode ? 'Select role(s)' : 'Select user(s)'}
                                placeholder="type to search"
                                variant="outlined"
                            />
                        )}
                    />
                </div>
            </Paper>

            {/* ------------------------- step 2 ------------------------- */}
            <Paper className={classes.card} elevation={0}>
                <div className={classes.cardHead}>
                    <span className={classes.stepNo} style={{ background: hasTarget ? NAVY : '#c3ccd9' }}>2</span>
                    <span className={classes.stepTitle} style={{ color: hasTarget ? NAVY : '#9aa7ba' }}>
                        Choose pages
                    </span>
                    {hasTarget && (
                        <Chip
                            size="small"
                            className={classes.pushRight}
                            label={`${selectedPaths.length} of ${routes.length} selected`}
                            style={{
                                background: selectedPaths.length ? '#fff0e6' : '#eceff3',
                                color: selectedPaths.length ? '#b4501f' : '#6b7a90',
                                fontWeight: 500
                            }}
                        />
                    )}
                </div>

                {!hasTarget ? (
                    <div className={classes.empty}>
                        Select one or more <b>{isRoleMode ? 'roles' : 'users'}</b> above to start giving access
                    </div>
                ) : (
                    <>
                        {isMulti && (
                            <div className={classes.cardBody} style={{ paddingBottom: 0, color: '#6b7a90', fontSize: 13 }}>
                                {targets.length} selected: ticked pages will be <b>added</b> to each one. Existing access is kept
                                (pages ticked here are the ones all of them already have).
                            </div>
                        )}
                        <div
                            className={classes.cardBody}
                            style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', paddingBottom: 8 }}
                        >
                            <Autocomplete
                                className={classes.routeBox}
                                size="small"
                                multiple
                                disableCloseOnSelect
                                limitTags={2}
                                options={routes}
                                value={selectedRouteObjects}
                                filterOptions={routeFilter}
                                getOptionLabel={(option) => option.label || option.path}
                                getOptionSelected={(option, value) => option.path === value.path}
                                onChange={onRoutesPicked}
                                renderOption={(option, { selected }) => (
                                    <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                        <Checkbox size="small" checked={selected} style={{ marginRight: 4 }} />
                                        <span className={classes.routeLabel}>{option.label || option.path}</span>
                                        <span className={classes.routePath} style={{ marginLeft: 6 }}>{option.path}</span>
                                    </span>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Quick add" placeholder="search by name or path" variant="outlined" />
                                )}
                            />

                            <TextField
                                className={classes.search}
                                size="small"
                                variant="outlined"
                                placeholder="Filter the list below..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ color: '#9aa7ba', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearch('')}>
                                                <ClearIcon style={{ fontSize: 16 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        color="primary"
                                        checked={onlySelected}
                                        onChange={(e) => setOnlySelected(e.target.checked)}
                                    />
                                }
                                label={<span style={{ fontSize: 13, color: '#4a5a70' }}>Only selected</span>}
                            />

                            <Button
                                size="small"
                                disabled={isFiltering || !grouped.length}
                                onClick={() => setExpanded(allExpanded ? [] : grouped.map(g => g.name))}
                            >
                                {allExpanded ? 'Collapse all' : 'Expand all'}
                            </Button>

                            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#93a1b5' }}>
                                {visibleCount} route{visibleCount === 1 ? '' : 's'}
                            </span>
                        </div>

                        <div style={{ padding: '0 16px 16px 16px' }}>
                            {!grouped.length && (
                                <div className={classes.empty}>No routes match this filter</div>
                            )}

                            {grouped.map(group => {
                                const modulePaths = group.routes.map(r => r.path)
                                const picked = modulePaths.filter(p => selectedPaths.includes(p)).length
                                const allTicked = picked === modulePaths.length
                                const someTicked = picked > 0 && !allTicked

                                return (
                                    <Accordion
                                        key={group.name}
                                        className={classes.module}
                                        expanded={isOpen(group.name)}
                                        onChange={() => !isFiltering && toggleOpen(group.name)}
                                        TransitionProps={{ unmountOnExit: true }}
                                    >
                                        <AccordionSummary
                                            className={classes.moduleSummary}
                                            expandIcon={isFiltering ? null : <ExpandMoreIcon />}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={allTicked}
                                                indeterminate={someTicked}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => toggleModule(modulePaths, allTicked)}
                                            />
                                            <span className={classes.moduleName}>{group.name}</span>
                                            <Chip
                                                size="small"
                                                label={`${picked} / ${modulePaths.length}`}
                                                style={{
                                                    marginLeft: 'auto', marginRight: 8, height: 20, fontSize: 11,
                                                    background: picked ? '#fff0e6' : '#eceff3',
                                                    color: picked ? '#b4501f' : '#7c8a9e'
                                                }}
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails style={{ paddingTop: 0 }}>
                                            <div className={classes.routeGrid}>
                                                {group.routes.map(route => {
                                                    const on = selectedPaths.includes(route.path)
                                                    return (
                                                        <div
                                                            key={route.path}
                                                            className={`${classes.routeItem} ${on ? classes.routeItemOn : ''}`}
                                                            onClick={() => togglePath(route.path)}
                                                        >
                                                            <Checkbox size="small" checked={on} style={{ padding: 4 }} />
                                                            <div style={{ minWidth: 0 }}>
                                                                <div className={classes.routeLabel}>
                                                                    {route.label || route.path}
                                                                    {route.openToAll && (
                                                                        <Chip
                                                                            size="small"
                                                                            label="everyone"
                                                                            style={{ height: 16, fontSize: 10, marginLeft: 6 }}
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className={classes.routePath}>{route.path}</div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                )
                            })}
                        </div>
                    </>
                )}
            </Paper>

            {hasTarget && (
                <div className={classes.sticky}>
                    <Button variant="contained" color="primary" disabled={!isDirty} onClick={save}>
                        Save access
                    </Button>
                    <Button disabled={!isDirty} onClick={() => setSelectedPaths(savedPaths)}>
                        Reset
                    </Button>
                    <span style={{ fontSize: 13, color: isDirty ? '#b4501f' : '#2e7d32', display: 'flex', alignItems: 'center' }}>
                        <span className={classes.dot} style={{ background: isDirty ? ORANGE : '#4caf50' }} />
                        {isDirty ? 'Unsaved changes' : 'All saved'}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#93a1b5' }}>
                        Users need to refresh the page after saving
                    </span>
                </div>
            )}

            <Dialog open={removeModal} onClose={() => setRemoveModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Remove these routes?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        These {staleRoutes.length} routes will be permanently deleted from the database
                        along with the access granted on them. They do not exist in the app, so nothing
                        will break &mdash; but if you plan to bring these pages back, do not remove them yet.
                    </DialogContentText>
                    <div style={{ maxHeight: 220, overflow: 'auto', background: '#fafbfd', borderRadius: 6, padding: 10, border: '1px solid #eef1f5' }}>
                        {staleRoutes.map(path => (
                            <div key={path} style={{ fontSize: 13, color: '#55657c', padding: '2px 0' }}>{path}</div>
                        ))}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveModal(false)}>Cancel</Button>
                    <Button onClick={removeStaleRoutes} color="secondary" variant="contained">
                        Yes, remove
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default connect(null, { setLoading, setSnackInfo })(RouteAccessManagement)
